import {
	ArrowLeftIcon,
	BenchmarkVsCompanyChart,
	Button,
	formatUTCDate,
	Link,
	OverallDistributionView,
	PdfIcon,
	ScoreByRoleChart,
	Search,
	SpinnerWithBackground,
	TabBar,
	Text,
	VsOverall,
	ZipIcon,
} from '@viapeople/components'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { sessionStore } from '../../../sharedStores/sessionStore'
import { CsvIcon, type ExportMenuItem, ExportSplitButton } from './components/ExportSplitButton'
import { DISTRIBUTION_TIERS, DistributionBar, type DistributionCounts, getRatingColor } from './components/shared'
import { useRatingScaleMax } from './components/useRatingScaleMax'
import { useDealTeamAccess } from './hooks/useDealTeamAccess'
import type { AggregationStore } from './stores/aggregationStore'
import { useAggregationStore } from './stores/aggregationStore'

type DealDetails = NonNullable<AggregationStore['dealDetails']>
type DealParticipant = DealDetails['participants'][number]
type DealInsight = DealDetails['insights'][number]
type Summary = NonNullable<AggregationStore['summary']>

function deriveRoleScores(participants: DealParticipant[]): Summary['roleScores'] {
	const groups = new Map<string, number[]>()
	for (const p of participants) {
		if (p.avgRating === null) continue
		const role = p.roleName ?? 'Unassigned'
		const existing = groups.get(role)
		if (existing) {
			existing.push(p.avgRating)
		} else {
			groups.set(role, [p.avgRating])
		}
	}
	return Array.from(groups.entries())
		.map(([roleName, ratings]) => ({
			roleName,
			avgRating: Number((ratings.reduce((s, r) => s + r, 0) / ratings.length).toFixed(2)),
			highestRating: Math.max(...ratings),
			lowestRating: Math.min(...ratings),
		}))
		.sort((a, b) => a.roleName.localeCompare(b.roleName))
}

function deriveOverallDistribution(participants: DealParticipant[]) {
	const counts = [0, 0, 0, 0, 0]
	for (const p of participants) {
		if (p.avgRating === null) continue
		// Buckets match RATING_DISTRIBUTION_BUCKETS server-side: [0,2], (2,4], (4,6], (6,8], (8,10]
		if (p.avgRating <= 2) counts[0]++
		else if (p.avgRating <= 4) counts[1]++
		else if (p.avgRating <= 6) counts[2]++
		else if (p.avgRating <= 8) counts[3]++
		else counts[4]++
	}
	const total = counts.reduce((s, c) => s + c, 0)
	return DISTRIBUTION_TIERS.map((tier, i) => ({
		label: tier.label,
		count: counts[i],
		percentage: total > 0 ? Math.round((counts[i] / total) * 100) : 0,
	}))
}

function insightDistributionToCounts(buckets: DealInsight['distribution']): DistributionCounts {
	const counts: DistributionCounts = {
		critical: 0,
		needsImprovement: 0,
		average: 0,
		good: 0,
		excellent: 0,
		total: 0,
	}
	for (const b of buckets) {
		switch (b.label) {
			case 'Critical':
				counts.critical = b.count
				break
			case 'Needs Improvement':
				counts.needsImprovement = b.count
				break
			case 'Average':
				counts.average = b.count
				break
			case 'Good':
				counts.good = b.count
				break
			case 'Excellent':
				counts.excellent = b.count
				break
		}
		counts.total += b.count
	}
	return counts
}

function RatingChip({ value }: { value: number | null }) {
	const scaleMax = useRatingScaleMax()
	if (value === null) return <span className='text-sm text-neutral-400'>—</span>
	return (
		<span
			className={`inline-flex items-center justify-center rounded-full px-2.5 py-1 text-xs font-semibold ${getRatingColor(value, scaleMax)}`}>
			{value.toFixed(2)}
		</span>
	)
}

function DealInsightsSection({ insights }: { insights: DealInsight[] }) {
	if (insights.length === 0) {
		return (
			<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
				<Text variant='large' weight='semibold'>
					Performance by Question
				</Text>
				<div className='flex items-center justify-center py-10 text-neutral-400'>
					<Text variant='small'>No rating questions found for this deal.</Text>
				</div>
			</div>
		)
	}

	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-5 text-left'>
			<Text variant='large' weight='semibold'>
				Performance by Question
			</Text>
			<div className='flex flex-wrap gap-x-6 gap-y-2'>
				{DISTRIBUTION_TIERS.map((tier) => (
					<div key={tier.key} className='flex items-center gap-1.5'>
						<div className='h-3 w-3 rounded shrink-0' style={{ backgroundColor: tier.color }} />
						<span className='text-xs text-neutral-600'>{tier.label}</span>
					</div>
				))}
			</div>
			<div className='rounded-lg border border-neutral-200 flex flex-col divide-y divide-neutral-100'>
				{insights.map((q, idx) => (
					<div key={`${q.questionLabel}-${idx}`} className='px-6 py-5 flex flex-col gap-3'>
						<p className='text-base font-normal text-neutral-900'>{q.questionLabel}</p>
						<div className='flex items-center gap-6'>
							<div className='flex flex-col gap-1 flex-1 min-w-0'>
								<span className='text-xs text-neutral-400'>Rating Distribution</span>
								<DistributionBar distribution={insightDistributionToCounts(q.distribution)} />
							</div>
							<div className='flex flex-col items-center gap-1 shrink-0'>
								<span className='text-xs text-neutral-500'>Avg</span>
								<RatingChip value={q.avgRating} />
							</div>
							<div className='flex flex-col items-center gap-1 shrink-0'>
								<span className='text-xs text-neutral-500'>Highest</span>
								<RatingChip value={q.highestRating} />
							</div>
							<div className='flex flex-col items-center gap-1 shrink-0'>
								<span className='text-xs text-neutral-500'>Lowest</span>
								<RatingChip value={q.lowestRating} />
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	)
}

function StatLabel({ children }: { children: React.ReactNode }) {
	return <span className='text-xs text-neutral-500'>{children}</span>
}

function StatusPill({ isCompleted }: { isCompleted: boolean }) {
	const className = isCompleted ? 'bg-emerald-100 text-emerald-700' : 'bg-blue-100 text-blue-700'
	return (
		<span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>
			{isCompleted ? 'Completed' : 'In Progress'}
		</span>
	)
}

type StatusFilter = '' | 'completed' | 'in-progress'

function DealParticipantsTab({ participants }: { participants: DealParticipant[] }) {
	const { cycleId } = useParams<{ cycleId: string }>()
	const navigate = useNavigate()
	const [searchInput, setSearchInput] = useState('')
	const [roleFilter, setRoleFilter] = useState<string>('')
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('')
	const [filtersOpen, setFiltersOpen] = useState(false)
	const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
	const filtersRef = useRef<HTMLDivElement>(null)
	const headerCheckboxRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (filtersRef.current && !filtersRef.current.contains(e.target as Node)) {
				setFiltersOpen(false)
			}
		}
		if (filtersOpen) document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [filtersOpen])

	const availableRoles = useMemo(() => {
		const roles = new Set<string>()
		for (const p of participants) {
			if (p.roleName) roles.add(p.roleName)
		}
		return Array.from(roles).sort((a, b) => a.localeCompare(b))
	}, [participants])

	const filtered = useMemo(() => {
		const q = searchInput.trim().toLowerCase()
		return participants.filter((p) => {
			if (roleFilter && p.roleName !== roleFilter) return false
			if (statusFilter === 'completed' && !p.isCompleted) return false
			if (statusFilter === 'in-progress' && p.isCompleted) return false
			if (q) {
				const haystack = [p.firstName, p.lastName, p.email, p.roleName].filter(Boolean).join(' ').toLowerCase()
				if (!haystack.includes(q)) return false
			}
			return true
		})
	}, [participants, searchInput, roleFilter, statusFilter])

	const activeFilterCount = (roleFilter ? 1 : 0) + (statusFilter ? 1 : 0)

	useEffect(() => {
		setSelectedIds((prev) => {
			if (prev.size === 0) return prev
			const valid = new Set(participants.map((p) => p.userId))
			let changed = false
			const next = new Set<string>()
			for (const id of prev) {
				if (valid.has(id)) next.add(id)
				else changed = true
			}
			return changed ? next : prev
		})
	}, [participants])

	const filteredIds = useMemo(() => filtered.map((p) => p.userId), [filtered])
	const selectedInFilter = useMemo(() => filteredIds.filter((id) => selectedIds.has(id)), [filteredIds, selectedIds])
	const allFilteredSelected = filteredIds.length > 0 && selectedInFilter.length === filteredIds.length
	const partialFilteredSelected = selectedInFilter.length > 0 && !allFilteredSelected

	useEffect(() => {
		if (headerCheckboxRef.current) {
			headerCheckboxRef.current.indeterminate = partialFilteredSelected
		}
	}, [partialFilteredSelected])

	const toggleAllFiltered = () => {
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (allFilteredSelected) {
				for (const id of filteredIds) next.delete(id)
			} else {
				for (const id of filteredIds) next.add(id)
			}
			return next
		})
	}

	const toggleOne = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev)
			if (next.has(id)) next.delete(id)
			else next.add(id)
			return next
		})
	}

	const clearSelection = () => setSelectedIds(new Set())

	const selectedIdsArray = useMemo(() => Array.from(selectedIds), [selectedIds])

	if (participants.length === 0) {
		return (
			<div className='rounded-lg border border-neutral-200 bg-white p-12 text-center text-neutral-500'>
				<Text variant='small'>No participants on this deal.</Text>
			</div>
		)
	}

	return (
		<div className='flex flex-col gap-4'>
			<div className='flex items-center gap-3'>
				<div className='flex-1'>
					<Search value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder='Search' />
				</div>

				<div ref={filtersRef} className='relative'>
					<button
						type='button'
						onClick={() => setFiltersOpen((v) => !v)}
						className='flex items-center gap-2 rounded-md border border-neutral-300 px-3 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50'>
						<svg
							width='16'
							height='16'
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							role='img'
							aria-label='Filters'>
							<line x1='4' y1='6' x2='20' y2='6' />
							<line x1='8' y1='12' x2='16' y2='12' />
							<line x1='11' y1='18' x2='13' y2='18' />
						</svg>
						Filters
						{activeFilterCount > 0 && (
							<span className='flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white'>
								{activeFilterCount}
							</span>
						)}
					</button>

					{filtersOpen && (
						<div className='absolute right-0 top-full z-10 mt-1 w-72 rounded-lg border border-neutral-200 bg-white p-4 shadow-lg'>
							<div className='flex flex-col gap-4'>
								<div className='flex flex-col gap-1'>
									<label htmlFor='participants-filter-role' className='text-xs font-medium text-neutral-600'>
										Role
									</label>
									<select
										id='participants-filter-role'
										value={roleFilter}
										onChange={(e) => setRoleFilter(e.target.value)}
										className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'>
										<option value=''>All Roles</option>
										{availableRoles.map((r) => (
											<option key={r} value={r}>
												{r}
											</option>
										))}
									</select>
								</div>
								<div className='flex flex-col gap-1'>
									<label htmlFor='participants-filter-status' className='text-xs font-medium text-neutral-600'>
										Status
									</label>
									<select
										id='participants-filter-status'
										value={statusFilter}
										onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
										className='rounded-md border border-neutral-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500'>
										<option value=''>All Statuses</option>
										<option value='completed'>Completed</option>
										<option value='in-progress'>In Progress</option>
									</select>
								</div>
								{activeFilterCount > 0 && (
									<button
										type='button'
										onClick={() => {
											setRoleFilter('')
											setStatusFilter('')
										}}
										className='text-xs text-blue-600 hover:underline text-left'>
										Clear all filters
									</button>
								)}
							</div>
						</div>
					)}
				</div>
			</div>

			{selectedIdsArray.length > 0 && cycleId && (
				<div className='flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-4 py-3'>
					<div className='flex items-center gap-3 text-sm text-neutral-700'>
						<span>
							<strong>{selectedIdsArray.length}</strong>{' '}
							{selectedIdsArray.length === 1 ? 'participant' : 'participants'} selected
						</span>
						<button
							type='button'
							onClick={clearSelection}
							className='text-xs text-blue-600 hover:underline font-medium'>
							Clear selection
						</button>
					</div>
					<ExportMenu cycleId={cycleId} selectedUserIds={selectedIdsArray} />
				</div>
			)}

			<div className='overflow-x-auto rounded-lg border border-neutral-200 bg-white text-left'>
				<table className='w-full text-sm'>
					<thead className='bg-neutral-50'>
						<tr>
							<th className='w-12 px-4 py-4 text-left'>
								<input
									ref={headerCheckboxRef}
									type='checkbox'
									aria-label={
										allFilteredSelected ? 'Deselect all visible participants' : 'Select all visible participants'
									}
									checked={allFilteredSelected}
									onChange={toggleAllFiltered}
									disabled={filteredIds.length === 0}
									className='h-4 w-4 cursor-pointer rounded border-neutral-300 text-blue-600 focus:ring-blue-500'
								/>
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								Name
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								Role
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								Reviewers
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								Rating
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								vs. Deal Overall
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								Status
							</th>
							<th className='px-6 py-4 text-left text-xs font-medium uppercase text-neutral-500 tracking-wider'>
								Report
							</th>
						</tr>
					</thead>
					<tbody className='divide-y divide-neutral-200'>
						{filtered.length === 0 && (
							<tr>
								<td colSpan={8} className='px-6 py-8 text-center text-neutral-500'>
									No participants match the current filters.
								</td>
							</tr>
						)}
						{filtered.map((p) => {
							const fullName = [p.firstName, p.lastName].filter(Boolean).join(' ') || p.email
							const isSelected = selectedIds.has(p.userId)
							return (
								<tr key={p.userId} className={`hover:bg-neutral-50 ${isSelected ? 'bg-blue-50/40' : ''}`}>
									<td className='w-12 px-4 py-4'>
										<input
											type='checkbox'
											aria-label={`${isSelected ? 'Deselect' : 'Select'} ${fullName}`}
											checked={isSelected}
											onChange={() => toggleOne(p.userId)}
											className='h-4 w-4 cursor-pointer rounded border-neutral-300 text-blue-600 focus:ring-blue-500'
										/>
									</td>
									<td className='px-6 py-4 font-medium text-neutral-900'>{fullName}</td>
									<td className='px-6 py-4 text-neutral-700'>{p.roleName ?? '—'}</td>
									<td className='px-6 py-4 text-neutral-700'>
										{p.submittedReviewerCount}/{p.reviewerCount}
									</td>
									<td className='px-6 py-4'>
										<RatingChip value={p.avgRating} />
									</td>
									<td className='px-6 py-4'>
										<VsOverall value={p.vsOverall} />
									</td>
									<td className='px-6 py-4'>
										<StatusPill isCompleted={p.isCompleted} />
									</td>
									<td className='px-6 py-4'>
										<Button
											variant='secondary'
											size='small'
											disabled={p.submittedReviewerCount === 0}
											onClick={() => navigate(`/dealteam/deals/${cycleId}/participants/${p.userId}/report`)}>
											Report
										</Button>
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>
			</div>
		</div>
	)
}

function DealHeader({ deal }: { deal: DealDetails }) {
	return (
		<div className='flex flex-col gap-3 text-left items-start'>
			<Text variant='h3' weight='semibold' className='text-left'>
				{deal.dealName}
			</Text>
			{deal.description && (
				<Text variant='medium' className='text-left text-neutral-600 max-w-5xl'>
					{deal.description}
				</Text>
			)}
		</div>
	)
}

function ExportMenu({ cycleId, selectedUserIds }: { cycleId: string; selectedUserIds?: string[] }) {
	const {
		exportDealReportPdf,
		exportDealParticipantsCsv,
		exportDealParticipantsReportsZip,
		isExportingDealReportPdf,
		isExportingDealParticipantsCsv,
		isExportingDealParticipantsZip,
	} = useAggregationStore()

	const hasSelection = !!selectedUserIds && selectedUserIds.length > 0
	const selectionCount = selectedUserIds?.length ?? 0
	const selectionScopeLabel = hasSelection ? (
		<>
			selected <strong>({selectionCount})</strong> participants
		</>
	) : (
		'all participants'
	)

	const items: ExportMenuItem[] = [
		{
			key: 'pdf',
			label: 'Export deal report (.pdf)',
			ariaLabel: 'Export deal report (.pdf)',
			icon: <PdfIcon size={20} className='fill-neutral-500' />,
			action: () => exportDealReportPdf(cycleId),
			busy: isExportingDealReportPdf,
		},
		{
			key: 'csv',
			label: <>Export {selectionScopeLabel} spreadsheet (.csv)</>,
			ariaLabel: hasSelection
				? `Export selected (${selectionCount}) participants spreadsheet (.csv)`
				: 'Export all participants spreadsheet (.csv)',
			icon: <CsvIcon className='text-neutral-500' />,
			action: () => exportDealParticipantsCsv(cycleId, selectedUserIds),
			busy: isExportingDealParticipantsCsv,
		},
		{
			key: 'zip',
			label: <>Export {selectionScopeLabel} reports (.zip)</>,
			ariaLabel: hasSelection
				? `Export selected (${selectionCount}) participants reports (.zip)`
				: 'Export all participants reports (.zip)',
			icon: <ZipIcon size={20} className='fill-neutral-500' />,
			action: () => exportDealParticipantsReportsZip(cycleId, { userIds: selectedUserIds }),
			busy: isExportingDealParticipantsZip,
		},
	]

	return <ExportSplitButton items={items} />
}

function DealStatsCard({ deal }: { deal: DealDetails }) {
	return (
		<div className='rounded-lg border border-neutral-200 bg-white p-6 text-left'>
			<div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-5'>
				<div className='flex flex-col gap-2'>
					<StatLabel>Close Date</StatLabel>
					<span className='text-base font-semibold text-neutral-900'>{formatUTCDate(deal.closeDate)}</span>
				</div>
				<div className='flex flex-col gap-2'>
					<StatLabel>Participants</StatLabel>
					<span className='text-base font-semibold text-neutral-900'>{deal.participantCount}</span>
				</div>
				<div className='flex flex-col gap-2'>
					<StatLabel>Deal Overall Rating</StatLabel>
					<div>
						<RatingChip value={deal.overallRating} />
					</div>
				</div>
				<div className='flex flex-col gap-2'>
					<StatLabel>Highest Rated Participant</StatLabel>
					{deal.highestRatedParticipant ? (
						<div className='flex items-center gap-2 min-w-0'>
							<RatingChip value={deal.highestRatedParticipant.score} />
							<span className='text-sm font-semibold text-neutral-900 truncate'>
								{deal.highestRatedParticipant.name}
							</span>
						</div>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
				<div className='flex flex-col gap-2'>
					<StatLabel>Lowest Rated Participant</StatLabel>
					{deal.lowestRatedParticipant ? (
						<div className='flex items-center gap-2 min-w-0'>
							<RatingChip value={deal.lowestRatedParticipant.score} />
							<span className='text-sm font-semibold text-neutral-900 truncate'>
								{deal.lowestRatedParticipant.name}
							</span>
						</div>
					) : (
						<span className='text-sm text-neutral-400'>—</span>
					)}
				</div>
			</div>
		</div>
	)
}

type DetailsTab = 'insights' | 'participants'

export function DealDetailsPage() {
	const scaleMax = useRatingScaleMax()
	const { cycleId } = useParams<{ cycleId: string }>()
	const navigate = useNavigate()
	const organizationId = sessionStore((s) => s.user?.organizationId)
	const { isAuthorized, isLoading: accessLoading } = useDealTeamAccess()
	const [activeTab, setActiveTab] = useState<DetailsTab>('insights')

	const {
		summary,
		dealDetails,
		isDealDetailsLoading,
		initialize,
		loadDealDetails,
		clearDealDetails,
		dealDistributionBuckets,
		isLoading,
		error,
		errorType,
	} = useAggregationStore()

	useEffect(() => {
		if (organizationId) initialize(organizationId)
	}, [organizationId, initialize])

	useEffect(() => {
		if (cycleId) loadDealDetails(cycleId)
		return () => clearDealDetails()
	}, [cycleId, loadDealDetails, clearDealDetails])

	const roleScores = useMemo(() => (dealDetails ? deriveRoleScores(dealDetails.participants) : []), [dealDetails])
	const distribution = useMemo(
		() => (dealDetails ? deriveOverallDistribution(dealDetails.participants) : []),
		[dealDetails],
	)

	if (accessLoading || isLoading) return <SpinnerWithBackground />

	if (!isAuthorized) {
		return (
			<main className='flex flex-col items-center justify-center p-8 gap-4'>
				<Text variant='large' weight='semibold'>
					Access Denied
				</Text>
				<Text variant='small' className='text-neutral-500'>
					You don't have permission to access the Deal Team Aggregation Portal.
				</Text>
				<Button variant='secondary' onClick={() => navigate('/dashboard')}>
					Back to Dashboard
				</Button>
			</main>
		)
	}

	if (error) {
		if (errorType === 'forbidden') {
			return (
				<main className='flex flex-col items-center justify-center p-8 gap-4'>
					<Text variant='large' weight='semibold'>
						Access Denied
					</Text>
					<Text variant='small' className='text-neutral-500'>
						You don't have permission to access the Deal Team Aggregation Portal.
					</Text>
					<Button variant='secondary' onClick={() => navigate('/dashboard')}>
						Back to Dashboard
					</Button>
				</main>
			)
		}
		return (
			<main className='flex flex-col items-center justify-center p-8 gap-4'>
				<Text variant='large' weight='semibold' className='text-red-600'>
					Something went wrong
				</Text>
				<Text variant='small' className='text-neutral-500'>
					{error}
				</Text>
				<Button variant='secondary' onClick={() => organizationId && initialize(organizationId)}>
					Retry
				</Button>
			</main>
		)
	}

	return (
		<main className='flex flex-col gap-6 p-6'>
			<div className='flex items-start justify-between gap-4'>
				<div className='flex items-start gap-3'>
					<button
						type='button'
						aria-label='Back to Aggregation Portal'
						className='mt-0.5 flex h-9 w-9 items-center justify-center rounded-md border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 hover:text-neutral-800'
						onClick={() => navigate('/dealteam?tab=data-explorer')}>
						<ArrowLeftIcon size={20} className='text-neutral-600' />
					</button>
					<div className='flex items-center'>
						<Link href='/dashboard' variant='breadcrumb' size='small' withChevron>
							Dashboard
						</Link>
						<Link href='/dealteam' variant='breadcrumb' size='small' withChevron>
							DealTeam
						</Link>
						<Link href='/dealteam?tab=data-explorer' variant='breadcrumb' size='small' withChevron>
							Aggregation Portal
						</Link>
						<Link variant='breadcrumb' size='small' disabled>
							{dealDetails?.dealName ?? 'Deal'}
						</Link>
					</div>
				</div>
				{cycleId && <ExportMenu cycleId={cycleId} />}
			</div>

			{isDealDetailsLoading && <SpinnerWithBackground />}

			{!isDealDetailsLoading && !dealDetails && (
				<div className='py-16 text-center text-neutral-500'>
					<Text variant='small'>No deal data available.</Text>
				</div>
			)}

			{!isDealDetailsLoading && dealDetails && summary && (
				<div className='flex flex-col gap-6'>
					<DealHeader deal={dealDetails} />
					<DealStatsCard deal={dealDetails} />

					<TabBar
						tabs={[
							{ id: 'insights', label: 'Deal Insights' },
							{ id: 'participants', label: 'Deal Participants' },
						]}
						activeTab={activeTab}
						onChange={(tab) => setActiveTab(tab as DetailsTab)}
						aria-label='Deal sections'
					/>

					{activeTab === 'insights' && (
						<>
							<div className='rounded-lg border border-neutral-200 bg-white p-6 flex flex-col gap-4 text-left'>
								<Text variant='large' weight='semibold'>
									Deal Performance
								</Text>
								<div className='grid grid-cols-1 gap-4 lg:grid-cols-2'>
									<BenchmarkVsCompanyChart
										deals={
											dealDetails.overallRating !== null
												? [{ dealName: dealDetails.dealName, rating: dealDetails.overallRating }]
												: []
										}
										overallRating={summary.overallRating}
										top10PercentRating={summary.top10PercentRating}
										scaleMax={scaleMax}
									/>
									<ScoreByRoleChart
										roleScores={roleScores}
										overallRating={dealDetails.overallRating}
										scaleMax={scaleMax}
									/>
								</div>
								<OverallDistributionView
									distribution={distribution}
									distributionBuckets={dealDistributionBuckets}
									scaleMax={scaleMax}
									onViewProfile={(userId) => {
										const url = new URL(window.location.href)
										url.pathname = '/dealteam'
										url.search = ''
										url.searchParams.set('tab', 'data-explorer')
										url.searchParams.set('participantId', userId)
										window.open(url.toString(), '_blank')
									}}
									onViewReport={(userId) => navigate(`/dealteam/deals/${cycleId}/participants/${userId}/report`)}
								/>
							</div>

							<DealInsightsSection insights={dealDetails.insights} />
						</>
					)}

					{activeTab === 'participants' && <DealParticipantsTab participants={dealDetails.participants} />}
				</div>
			)}
		</main>
	)
}
